/**
 * CaseFlow+AI - Improvement Suggestion Modal
 * Lightweight suggestion form for pilot feedback and PMF validation
 * Separate flow from bug reports
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { Lightbulb, X } from 'lucide-react';
import type { ImprovementSuggestionData, ImprovementArea } from '../../services/feedbackService';
import { useTheme } from '../../theme/ThemeProvider';
import { useTranslation } from '../../i18n/LanguageProvider';

interface ImprovementSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: ImprovementSuggestionData) => void;
}

const IMPROVEMENT_AREAS: { value: ImprovementArea; labelSv: string; labelEn: string }[] = [
  { value: 'ui', labelSv: 'Gränssnitt', labelEn: 'UI/UX' },
  { value: 'workflow', labelSv: 'Arbetsflöde', labelEn: 'Workflow' },
  { value: 'feature', labelSv: 'Funktion', labelEn: 'Feature' },
  { value: 'performance', labelSv: 'Prestanda', labelEn: 'Performance' },
  { value: 'other', labelSv: 'Annat', labelEn: 'Other' },
];

export function ImprovementSuggestionModal({
  isOpen,
  onClose,
  onSubmit,
}: ImprovementSuggestionModalProps) {
  const { theme } = useTheme();
  const { t, language } = useTranslation();

  // Form state
  const [description, setDescription] = useState('');
  const [area, setArea] = useState<ImprovementArea>('ui');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);

    try {
      const suggestionData: ImprovementSuggestionData = {
        type: 'improvement_suggestion',
        description: description.trim(),
        area,
        current_route: window.location.pathname + window.location.search,
        current_theme: theme,
        current_language: language,
      };

      onSubmit?.(suggestionData);

      // Reset form
      setDescription('');
      setArea('ui');
      onClose();
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            {language === 'sv' ? 'Förslag till förbättring' : 'Improvement Suggestion'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Area */}
          <div className="space-y-2">
            <Label htmlFor="improvement-area">
              {language === 'sv' ? 'Område' : 'Area'} *
            </Label>
            <select
              id="improvement-area"
              value={area}
              onChange={(e) => setArea(e.target.value as ImprovementArea)}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#0f0f11]"
              required
            >
              {IMPROVEMENT_AREAS.map(a => (
                <option key={a.value} value={a.value}>
                  {language === 'sv' ? a.labelSv : a.labelEn}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="improvement-description">
              {language === 'sv' ? 'Beskrivning' : 'Description'} *
            </Label>
            <Textarea
              id="improvement-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                language === 'sv'
                  ? 'Beskriv din idé till förbättring...'
                  : 'Describe your improvement idea...'
              }
              className="min-h-[120px]"
              required
            />
          </div>

          {/* Footer */}
          <DialogFooter className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 mr-2" />
              {language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={!description.trim() || isSubmitting}
            >
              <Lightbulb className="h-4 w-4 mr-2" />
              {isSubmitting
                ? (language === 'sv' ? 'Skickar...' : 'Submitting...')
                : (language === 'sv' ? 'Skicka förslag' : 'Submit Suggestion')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ImprovementSuggestionModal;
